import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../lib/cn';
import { useTheme } from '../../theme/ThemeProvider';

/* =============================================================================
   Brand mark
   -----------------------------------------------------------------------------
   The assets are loaded through <img>, never inlined. The pack's SVGs declare
   fixed internal ids (`afront0`, `aside0`, `aedge0`, …); inlining more than one
   of them on a page would collide, because duplicate ids in the same document
   resolve to the first match and the second mark would render with the wrong
   gradients.

   Which file to use is decided in React rather than by CSS, because the theme is
   already resolved before first paint by the inline head script — so the correct
   file is requested on the first render and there is no flash of the wrong mark.
   ============================================================================= */

const STATIC_LIGHT = '/brand/svg/alliance-3d-transparent.svg';
const STATIC_DARK = '/brand/svg/alliance-3d-transparent-reversed.svg';

/**
 * The animated marks ship with the original copper/graphite material only.
 * Measured against the dark canvas (#181B22), most of that graphite sits at
 * 1.04–1.56 contrast — effectively invisible — so they are restricted to light
 * surfaces until a reversed animated set exists.
 */
export const ANIMATED_MARKS = {
  interlock: { src: '/brand/animated/01-interlock.svg', label: 'Interlock', seconds: 5.6 },
  'continuous-rotation': {
    src: '/brand/animated/02-continuous-rotation.svg',
    label: 'Continuous rotation',
    seconds: 6,
  },
  'metallic-sweep': { src: '/brand/animated/03-metallic-sweep.svg', label: 'Metallic sweep', seconds: 5.4 },
  'blueprint-reveal': {
    src: '/brand/animated/04-blueprint-reveal.svg',
    label: 'Blueprint reveal',
    seconds: 6.4,
  },
  'async-match': { src: '/brand/animated/05-async-match.svg', label: 'Async match', seconds: 10 },
} as const;

export type AnimatedMarkName = keyof typeof ANIMATED_MARKS;

export interface BrandMarkProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /** Rendered size of the square mark, in pixels. */
  size?: number;
  /**
   * Plays an animated variant. Only valid on light surfaces — see the note
   * above. When omitted, the static theme-appropriate mark is used.
   */
  animated?: AnimatedMarkName;
  /** Overrides the accessible name. Defaults to "escrowbuild.io". */
  label?: string;
}

/** The square mark on its own — use inside the header, avatars, favicons. */
export function BrandMark({
  size = 32,
  animated,
  label = 'escrowbuild.io',
  className,
  /*
     `style` is destructured rather than left in `...rest`, because the root
     element sets its own inline size and an inline style written after the
     spread wins. That made `style` a declared prop that did nothing: a caller
     could pass a transform, an alignment or a custom property and see no
     effect, with nothing to indicate it had been dropped. Merging keeps the
     size as the default while letting a caller add to it, which is what the
     prop list promises.
  */
  style,
  ...rest
}: BrandMarkProps) {
  const { resolved } = useTheme();
  const src = animated
    ? ANIMATED_MARKS[animated].src
    : resolved === 'dark'
      ? STATIC_DARK
      : STATIC_LIGHT;

  return (
    <span
      {...rest}
      className={cn('inline-grid shrink-0 place-items-center', className)}
      style={{ width: size, height: size, ...style }}
    >
      <img
        src={src}
        width={size}
        height={size}
        alt={label}
        // The mark renders at a fraction of the 1024px master, so it is never
        // scaled up and never needs more than a 2x variant.
        decoding="async"
        className="size-full object-contain"
      />
    </span>
  );
}

export interface LogoProps extends Omit<ComponentPropsWithRef<'a'>, 'children'> {
  /** Rendered size of the square mark, in pixels. */
  size?: number;
  /**
   * The wordmark beside the mark.
   *
   * NOTE: the product name is held in one place — change it here and every
   * usage follows. The brand pack still records `brand.config.json` with
   * `brandName: null` and calls "Alliance" the concept label only, which is why
   * the name lives in the component rather than in the pack.
   */
  name?: string;
  /** Small line under the wordmark. Pass `null` for the mark alone. */
  tagline?: string | null;
  animated?: AnimatedMarkName;
  href?: string;
}

/**
 * The wordmark's line box, in pixels.
 *
 * `text-lg` is 18px and `leading-none` sets the line-height equal to the
 * font-size, so the name's line box is exactly this tall and its centre is
 * exactly half of it. It is a named constant rather than a literal because the
 * mark's offset below is derived from it — both are set in this file, a few
 * lines apart, so they cannot drift apart without the edit being visible.
 */
const WORDMARK_LINE = 18;

/** The full lockup: mark plus wordmark, linking home by default. */
export function Logo({
  size = 34,
  name = 'escrowbuild.io',
  tagline = 'Construction platform',
  animated,
  href = '/',
  className,
  ...rest
}: LogoProps) {
  return (
    <a
      {...rest}
      href={href}
      className={cn(
        /*
           `items-start` with the mark offset — NOT `items-center`.

           `items-center` centred the mark on the WHOLE text column, so the
           tagline's height pushed the wordmark off the mark. Measured on
           `Brand/Logo > Default`, the name sat 7.50px above the mark's centre
           while the tagline sat 11.00px below it: two elements at different
           heights rather than one mark with a name. `Without Tagline` measured
           0.00px, which is precisely why the defect only appeared once a
           tagline existed.

           The mark belongs on the WORDMARK, and the tagline belongs tight under
           the wordmark — so the mark is moved, not the text. Laying the name row
           out at the MARK's height instead fixes the centre and breaks the
           tagline: at size 56 it pushes the tagline 37.50px down and the lockup
           falls apart. Offsetting the mark keeps the text column's own spacing
           independent of how large the mark is drawn.
        */
        'dhd-target relative inline-flex items-start gap-3 rounded-xs no-underline',
        'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus',
        className,
      )}
    >
      {/* Half the difference between the wordmark's line and the mark's height:
          the mark's centre lands on the wordmark's centre at every size, and a
          mark taller than the text simply overhangs above it, which is what a
          mark is supposed to do. */}
      <BrandMark
        size={size}
        animated={animated}
        label={name}
        style={{ transform: `translateY(${(WORDMARK_LINE - size) / 2}px)` }}
      />
      <span className="flex min-w-0 flex-col leading-none">
        <span className="flex font-display text-lg font-semibold uppercase leading-none tracking-brand text-heading">
          {name}
        </span>
        {tagline ? (
          <span className="mt-1 text-2xs uppercase tracking-label text-muted">{tagline}</span>
        ) : null}
      </span>
    </a>
  );
}
